import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { cartApi, type Cart } from "@/lib/api";
import { itemCount, type QtyChange } from "@/lib/cart-upgrade";
import { trackAddToCart, trackCartItemRemoved } from "@/lib/tracking";

interface CartState {
  cart: Cart | null;
  /** True only during the very first load, so the header badge doesn't flash. */
  loading: boolean;
  /** True while a mutation is in flight, for disabling buttons. */
  busy: boolean;
  error: string | null;
  count: number;
  refresh: () => Promise<void>;
  add: (
    skuId: string,
    qty?: number,
    pasang?: boolean,
    addonIds?: string[],
  ) => Promise<Cart>;
  setQty: (skuId: string, qty: number) => Promise<void>;
  setPasang: (skuId: string, pasang: boolean) => Promise<void>;
  remove: (skuId: string) => Promise<void>;
  /**
   * Writes several stored quantities as one action — an upgrade's stepper
   * moves the desk and the stand together. Sequential, and the cart shown is
   * the one the last write returned, so the page never renders a half-step.
   */
  applyQty: (changes: QtyChange[]) => Promise<void>;
  /** Pasang on several lines at once, for an upgrade's desk. */
  setPasangMany: (skuIds: string[], pasang: boolean) => Promise<void>;
  /** Take the promotion's free gift out, or put it back. */
  setPromoGift: (include: boolean) => Promise<void>;
  clearError: () => void;
}

const CartContext = createContext<CartState | null>(null);

/**
 * Runs cart writes one after another and returns the cart the last one
 * produced. If one fails part-way, the earlier writes have already landed, so
 * the cart is re-read before the error surfaces — otherwise the page would go
 * on showing a cart that no longer exists.
 */
async function sequence(
  steps: (() => Promise<Cart>)[],
  setCart: (c: Cart) => void,
): Promise<Cart> {
  let last: Cart | null = null;
  try {
    for (const step of steps) last = await step();
  } catch (e) {
    if (last) {
      try {
        setCart(await cartApi.get());
      } catch {
        // The original error is the one worth showing.
      }
    }
    throw e;
  }
  return last ?? (await cartApi.get());
}

/**
 * Cart state for the whole storefront.
 *
 * The cart is never fetched during prerender. vite-react-ssg renders these
 * pages at build time, where there is no browser, no cookie and no API — so
 * the first fetch is deliberately deferred to an effect, and every consumer
 * has to cope with `cart` being null. That is also the correct runtime state
 * for the first paint, so the two agree and hydration doesn't mismatch.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(
    async (fn: () => Promise<Cart>, markBusy: boolean): Promise<Cart> => {
      if (markBusy) setBusy(true);
      setError(null);
      try {
        const next = await fn();
        setCart(next);
        return next;
      } catch (e) {
        setError(e instanceof Error ? e.message : "Keranjang gagal dimuat.");
        throw e;
      } finally {
        if (markBusy) setBusy(false);
      }
    },
    [],
  );

  // A mutation whose caller only needs it done. add() is the one that hands
  // the new cart back, because the product page compares before and after.
  const act = useCallback(
    async (fn: () => Promise<Cart>): Promise<void> => {
      await run(fn, true);
    },
    [run],
  );

  const refresh = useCallback(async () => {
    try {
      await run(cartApi.get, false);
    } catch {
      // A cart that cannot be read must not take the page down with it — the
      // catalogue still works, and the header simply shows no badge.
    } finally {
      setLoading(false);
    }
  }, [run]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<CartState>(() => {
    // Lines taken out of the cart, reported from the cart as it was before:
    // the one the server hands back no longer has them.
    const reportRemoved = (skuIds: string[]) => {
      for (const id of skuIds) {
        const line = cart?.items.find((i) => i.skuId === id);
        if (line)
          trackCartItemRemoved({ id: line.skuCode, name: line.productName, priceIdr: line.unitPriceIdr, qty: line.qty });
      }
    };
    return {
      cart,
      loading,
      busy,
      error,
      // A Plus is one item, not a desk and a stand.
      count: itemCount(cart),
      refresh,
      add: async (skuId, qty = 1, pasang, addonIds) => {
        const next = await run(() => cartApi.add(skuId, qty, pasang, addonIds), true);
        // Reported here, not by each button, so the product page and the
        // quick-add sheet can't disagree. The line's own price, from the cart
        // the server just returned.
        for (const id of [skuId, ...(addonIds ?? [])]) {
          const line = next.items.find((i) => i.skuId === id);
          if (line)
            trackAddToCart({
              id: line.skuCode,
              name: line.productName,
              priceIdr: line.unitPriceIdr,
              qty: id === skuId ? qty : 1,
            }, id !== skuId);
        }
        return next;
      },
      setQty: (skuId, qty) => act(() => cartApi.setQty(skuId, qty)),
      setPasang: (skuId, pasang) => act(() => cartApi.setPasang(skuId, pasang)),
      remove: async (skuId) => {
        await act(() => cartApi.remove(skuId));
        reportRemoved([skuId]);
      },
      applyQty: async (changes) => {
        await act(
          () =>
            sequence(
              changes.map((c) => () =>
                c.qty > 0 ? cartApi.setQty(c.skuId, c.qty) : cartApi.remove(c.skuId),
              ),
              setCart,
            ),
        );
        reportRemoved(changes.filter((c) => c.qty <= 0).map((c) => c.skuId));
      },
      setPasangMany: (skuIds, pasang) =>
        act(() => sequence(skuIds.map((id) => () => cartApi.setPasang(id, pasang)), setCart)),
      setPromoGift: (include) => act(() => cartApi.setPromoGift(include)),
      clearError: () => setError(null),
    };
  }, [cart, loading, busy, error, refresh, run, act]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartState {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside a CartProvider");
  return ctx;
}
